import { XRayDiagnosticCode } from 'pages/Diagnostics/types'

type PathologyDescription = {
  name: string
  description: string
  identification: string
  sources: {
    radiopaedia: string
    nih_medlineplus: string | null
    fleischner_society: string | null
    radiology_assistant: string | null
    oms_who: string
  }
}

const chestXrayPathologiesDescriptions: Record<
  XRayDiagnosticCode,
  PathologyDescription
> = {
  cardiomegaly: {
    name: 'Cardiomegalia',
    description:
      'Aumento do tamanho da silhueta cardíaca, geralmente associado a doenças cardíacas, hipertensão ou insuficiência cardíaca.',
    identification:
      'Índice cardiotorácico superior a 0,5 em projeção PA (largura do coração maior que 50% da largura do tórax). A silhueta cardíaca apresenta contornos alargados e pode comprimir estruturas adjacentes.',
    sources: {
      radiopaedia: 'https://radiopaedia.org/articles/cardiomegaly',
      nih_medlineplus: null,
      fleischner_society: null,
      radiology_assistant:
        'https://radiopaedia-org.translate.goog/articles/cardiothoracic-ratio?_x_tr_sl=en&_x_tr_tl=pt&_x_tr_hl=pt&_x_tr_pto=tc',
      oms_who: 'https://www.who.int/health-topics/cardiovascular-diseases'
    }
  },
  nodule: {
    name: 'Nódulo',
    description:
      'Opacidade arredondada ou ovalada, bem ou mal delimitada, com diâmetro inferior a 3 cm, podendo ser benigna ou maligna.',
    identification:
      'Lesão focal arredondada, isolada ou múltipla, com densidade variável. Bordas regulares sugerem benignidade; bordas espiculadas ou irregulares aumentam a suspeita de malignidade.',
    sources: {
      radiopaedia: 'https://radiopaedia.org/articles/pulmonary-nodule',
      nih_medlineplus: null,
      fleischner_society: 'https://pubs.rsna.org/doi/full/10.1148/radiol.232558',
      radiology_assistant: null,
      oms_who: 'https://www.who.int/health-topics/cancer'
    }
  },
  infiltration: {
    name: 'Infiltração',
    description:
      'Presença de substância anormal (células inflamatórias, líquido ou tecido) no parênquima pulmonar, frequentemente associada a processos infecciosos ou inflamatórios.',
    identification:
      'Opacidade de limites imprecisos, sem broncograma aéreo definido, que pode apagar a silhueta de estruturas adjacentes (sinal da silhueta positivo). Distribuição variável — lobar, segmentar ou difusa.',
    sources: {
      radiopaedia: 'https://radiopaedia.org/articles/pulmonary-infiltrate',
      nih_medlineplus: null,
      fleischner_society: null,
      radiology_assistant: null,
      oms_who: 'https://www.who.int/health-topics/respiratory-health'
    }
  },
  pneumonia: {
    name: 'Pneumonia',
    description:
      'Infecção do parênquima pulmonar causada por agentes bacterianos, virais, fúngicos ou parasitas, com inflamação e exsudato alveolar.',
    identification:
      'Consolidação lobar ou segmentar com broncograma aéreo (árvore brônquica visível dentro da opacidade). Contornos mal definidos, com possível apagamento da hemicúpula diafragmática ou silhueta cardíaca.',
    sources: {
      radiopaedia: 'https://radiopaedia.org/articles/pneumonia',
      nih_medlineplus: 'https://medlineplus.gov/pneumonia.html',
      fleischner_society:
        'https://pubs.rsna.org/doi/epdf/10.1148/radiol.232558',
      radiology_assistant: null,
      oms_who: 'https://www.who.int/health-topics/pneumonia'
    }
  },
  atelectasis: {
    name: 'Atelectasia',
    description:
      'Colapso parcial ou total de uma região pulmonar por perda de ar alveolar, com consequente redução do volume pulmonar.',
    identification:
      'Opacidade linear ou em cunha com desvio das estruturas adjacentes em direção à área colapsada (traqueia, cissuras, hilo). Ausência de broncograma aéreo. Pode haver elevação ipsilateral do diafragma.',
    sources: {
      radiopaedia: 'https://radiopaedia.org/articles/atelectasis',
      nih_medlineplus: 'https://medlineplus.gov/ency/article/000065.htm',
      fleischner_society: 'https://pubs.rsna.org/doi/full/10.1148/radiol.232558',
      radiology_assistant:
        'https://radiologyassistant.nl/chest/chest-x-ray/lung-disease#atelectasis',
      oms_who: 'https://www.who.int/health-topics/respiratory-health'
    }
  },
  hernia: {
    name: 'Hérnia (hiatal)',
    description:
      'Protrusão de estruturas abdominais (estômago, intestino, epíploo) para a cavidade torácica, geralmente pelo hiato diafragmático ou por defeito congênito/traumático do diafragma.',
    identification:
      'Imagem de densidade mista (aérea e de partes moles) acima do diafragma, frequentemente com nível hidroaéreo. Interrupção ou descontinuidade do contorno diafragmático.',
    sources: {
      radiopaedia: 'https://radiopaedia.org/articles/diaphragmatic-hernia',
      nih_medlineplus: 'https://medlineplus.gov/ency/article/001135.htm',
      fleischner_society: null,
      radiology_assistant:
        'https://radiologyassistant.nl/chest/chest-x-ray/basic-interpretation#normal-and-variants-azygo-esophageal-recess',
      oms_who: 'https://www.who.int/health-topics/congenital-anomalies'
    }
  },
  consolidation: {
    name: 'Consolidação',
    description:
      'Substituição do ar alveolar por líquido, células ou tecido, resultando em opacidade homogênea do parênquima pulmonar.',
    identification:
      'Opacidade homogênea, densa, com broncograma aéreo frequentemente visível. Respeita limites lobares ou segmentares. Apaga silhuetas de estruturas adjacentes (coração, diafragma).',
    sources: {
      radiopaedia: 'https://radiopaedia.org/articles/pulmonary-consolidation',
      nih_medlineplus: null,
      fleischner_society:
        'https://pubs.rsna.org/doi/epdf/10.1148/radiol.232558',
      radiology_assistant:
        'https://radiologyassistant.nl/chest/chest-x-ray/lung-disease#consolidation',
      oms_who: 'https://www.who.int/health-topics/pneumonia'
    }
  },
  pleural_thickening: {
    name: 'Espessamento Pleural',
    description:
      'Aumento da espessura da pleura visceral ou parietal, geralmente sequela de processo inflamatório, infeccioso ou exposição a agentes como o asbesto. Também pode ser causada por irritação mecânica recorrente (fratura de costela), derrame pleural ou pneumotórax.',
    identification:
      'Opacidade periférica em faixa, com contorno interno irregular ou liso, acompanhando a parede torácica. Pode haver calcificações associadas. Obliteração do seio costofrênico em casos avançados.',
    sources: {
      radiopaedia: 'https://radiopaedia.org/articles/pleural-thickening',
      nih_medlineplus: null,
      fleischner_society:
        'https://pubs.rsna.org/doi/epdf/10.1148/radiol.232558',
      radiology_assistant: null,
      oms_who: 'https://www.who.int/health-topics/occupational-health'
    }
  },
  mass: {
    name: 'Massa',
    description:
      'Lesão focal pulmonar ou mediastinal com diâmetro igual ou superior a 3 cm, de etiologia variada (neoplásica, infecciosa ou benigna).',
    identification:
      'Opacidade volumosa, arredondada ou lobulada. Bordas espiculadas, irregulares ou com cavitação aumentam a suspeita de malignidade. Pode causar deslocamento de estruturas adjacentes.',
    sources: {
      radiopaedia: 'https://radiopaedia.org/articles/pulmonary-mass',
      nih_medlineplus: 'https://medlineplus.gov/lungcancer.html',
      fleischner_society: null,
      radiology_assistant: null,
      oms_who: 'https://www.who.int/health-topics/cancer'
    }
  },
  edema: {
    name: 'Edema (intersticial)',
    description:
      'Acúmulo de líquido no interstício e/ou nos alvéolos pulmonares, geralmente por causa cardiogênica (insuficiência cardíaca) ou não cardiogênica (SARA).',
    identification:
      'Opacidades bilaterais perihilares (asa de morcego), linhas B de Kerley, cardiomegalia associada (na causa cardiogênica) e redistribuição vascular para os lobos superiores. Derrame pleural pode estar presente.',
    sources: {
      radiopaedia: 'https://radiopaedia.org/articles/pulmonary-edema',
      nih_medlineplus: 'https://medlineplus.gov/ency/article/000140.htm',
      fleischner_society: null,
      radiology_assistant:
        'https://radiologyassistant.nl/chest/chest-x-ray/lung-disease#interstitial-disease',
      oms_who: 'https://www.who.int/health-topics/cardiovascular-diseases'
    }
  },
  fibrosis: {
    name: 'Fibrose',
    description:
      'Substituição permanente do tecido pulmonar normal por tecido conjuntivo cicatricial, com perda progressiva da função respiratória.',
    identification:
      'Padrão reticular ou reticulonodular bilateral, predominantemente nas bases. Redução dos volumes pulmonares, elevação das cúpulas diafragmáticas e aspecto em favo de mel nos casos avançados.',
    sources: {
      radiopaedia: 'https://radiopaedia.org/articles/pulmonary-fibrosis',
      nih_medlineplus: 'https://medlineplus.gov/ency/article/000069.htm',
      fleischner_society:
        'https://pubs.rsna.org/doi/epdf/10.1148/radiol.232558',
      radiology_assistant: null,
      oms_who: 'https://www.who.int/health-topics/chronic-respiratory-diseases'
    }
  },
  effusion: {
    name: 'Derrame',
    description:
      'Acúmulo de líquido no espaço pleural, podendo ser transudato (causas cardíacas/renais) ou exsudato (causas infecciosas/neoplásicas).',
    identification:
      'Opacidade homogênea basal com obliteração do seio costofrênico e curva de Damoiseau (bordas côncavas para cima). Em decúbito, o líquido se distribui na porção declive. Grandes derrames desviam o mediastino contralateralmente.',
    sources: {
      radiopaedia: 'https://radiopaedia.org/articles/pleural-effusion',
      nih_medlineplus: 'https://medlineplus.gov/ency/article/000086.htm',
      fleischner_society:
        'https://pubs.rsna.org/doi/epdf/10.1148/radiol.232558',
      radiology_assistant: null,
      oms_who: 'https://www.who.int/health-topics/respiratory-health'
    }
  },
  emphysema: {
    name: 'Enfisema',
    description:
      'Destruição permanente dos septos alveolares com aumento anormal dos espaços aéreos distais aos bronquíolos terminais, frequentemente associado ao tabagismo.',
    identification:
      'Hiperinsuflação pulmonar com aplanamento das cúpulas diafragmáticas, aumento do diâmetro anteroposterior do tórax (tórax em tonel), hiperlucência dos campos pulmonares e atenuação da trama vascular periférica.',
    sources: {
      radiopaedia: 'https://radiopaedia.org/articles/emphysema',
      nih_medlineplus: null,
      fleischner_society:
        'https://pubs.rsna.org/doi/epdf/10.1148/radiol.232558',
      radiology_assistant: null,
      oms_who:
        'https://www.who.int/health-topics/chronic-obstructive-pulmonary-disease'
    }
  },
  pneumothorax: {
    name: 'Pneumotórax',
    description:
      'Presença de ar no espaço pleural, separando a pleura visceral da parietal, com colapso pulmonar parcial ou total.',
    identification:
      'Linha pleural visceral visível, separada da parede torácica, sem trama pulmonar além dela. Em casos de pneumotórax hipertensivo: desvio do mediastino contralateral, rebaixamento do diafragma ipsilateral e alargamento dos espaços intercostais.',
    sources: {
      radiopaedia: 'https://radiopaedia.org/articles/pneumothorax',
      nih_medlineplus: 'https://medlineplus.gov/ency/article/000087.htm',
      fleischner_society:
        'https://pubs.rsna.org/doi/epdf/10.1148/radiol.232558',
      radiology_assistant: null,
      oms_who: 'https://www.who.int/health-topics/respiratory-health'
    }
  }
}

export default chestXrayPathologiesDescriptions
